import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { PostHogProvider, PostHogErrorBoundary } from "posthog-js/react";
import { SupabaseAuthProvider } from "./lib/auth";
import { initPostHog, isPostHogEnabled, posthog } from "./lib/posthog";
import { initSentry } from "./lib/sentry";
import { hasActiveCallSession } from "./lib/activeCallSession";
import App from "./App.tsx";
import "./index.css";

// Initialize Sentry before rendering so it captures all errors from first paint
initSentry();
initPostHog();

// Stale-chunk guard: when Vite deploys new chunks the old hashed filenames are
// gone. React.lazy() then throws "Importing a module script failed" which
// previously hit the error boundary and showed a crash screen. Instead, reload
// once to pick up fresh assets.
//
// Uses a timestamp-based expiry (not a permanent flag) so:
//   - A second stale-chunk error in the same session can still reload (e.g.
//     after multiple rapid deploys).
//   - Infinite loops are still prevented: if a reload fires within the
//     RELOAD_COOLDOWN_MS window, we don't reload again.
const CHUNK_RELOAD_KEY = 'reprev_chunk_reload_ts';
const CHUNK_RELOAD_COOLDOWN_MS = 10_000; // 10 s — enough to detect a loop

function maybeReloadForChunkError(): void {
  const lastStr = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  const last = lastStr ? parseInt(lastStr, 10) : 0;
  if (Date.now() - last > CHUNK_RELOAD_COOLDOWN_MS) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  }
}

function isChunkLoadMsg(msg: string): boolean {
  return (
    msg.includes('Importing a module script failed') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Unable to preload CSS')
  );
}

window.addEventListener('error', (event) => {
  if (isChunkLoadMsg(event.message ?? '')) maybeReloadForChunkError();
});
window.addEventListener('unhandledrejection', (event) => {
  const msg = String(event.reason?.message ?? event.reason ?? '');
  if (isChunkLoadMsg(msg)) maybeReloadForChunkError();
});

// Handle service worker registration errors gracefully — log to Sentry but never crash
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(
    () => { /* SW API available, registration handled by vite-plugin-pwa */ },
    (err) => {
      Sentry.captureException(err, { tags: { source: 'service-worker' } });
    }
  );
  navigator.serviceWorker.addEventListener('error', () => {
    Sentry.captureMessage('Service worker error', {
      level: 'warning',
      tags: { source: 'service-worker' },
    });
  });
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hasActiveCallSession()) {
      Sentry.captureMessage('Service worker changed during active call', {
        level: 'warning',
        tags: { source: 'service-worker', active_call: 'true' },
      });
    }
  });
}

const appTree = (
  <StrictMode>
    <SupabaseAuthProvider>
      <App />
    </SupabaseAuthProvider>
  </StrictMode>
);

createRoot(document.getElementById("root")!).render(
  isPostHogEnabled() ? (
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary>
        {appTree}
      </PostHogErrorBoundary>
    </PostHogProvider>
  ) : (
    appTree
  )
);
